import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Wheat, Plus, Calendar, Trash2, Edit2, Loader2, Check, X, AlertCircle,
  MapPin, TrendingUp, Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const culturas = [
  "Soja", "Milho", "Milho Safrinha", "Algodão", "Café", "Cana-de-açúcar",
  "Feijão", "Arroz", "Trigo", "Sorgo", "Girassol", "Amendoim", "Outro"
];

export default function Safras() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSafra, setEditingSafra] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cultura: "",
    data_inicio: "",
    data_fim: "",
    area_plantada: "",
    producao_esperada: "",
    producao_real: "",
    status: "planejada",
    talhoes: []
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState({
    nome: false,
    cultura: false,
    status: false
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: safras = [], isLoading } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId }, '-created_date')
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: talhoes = [] } = useQuery({
    queryKey: ['talhoes', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const createMutation = useMutation({
    mutationFn: (data) => agrogestao.entities.Safra.create({ ...data, propriedade_id: propriedadeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['safras'] });
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.Safra.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['safras'] });
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Safra.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safras'] })
  });

  const resetForm = () => {
    setFormData({
      nome: "", cultura: "", data_inicio: "", data_fim: "",
      area_plantada: "", producao_esperada: "", producao_real: "",
      status: "planejada", talhoes: []
    });
    setSubmitAttempted(false);
    setTouched({ nome: false, cultura: false, status: false });
    setErrors({});
    setEditingSafra(null);
    setDialogOpen(false);
  };

  const handleEdit = (safra) => {
    setFormData({
      nome: safra.nome || "",
      cultura: safra.cultura || "",
      data_inicio: safra.data_inicio || "",
      data_fim: safra.data_fim || "",
      area_plantada: safra.area_plantada?.toString() || "",
      producao_esperada: safra.producao_esperada?.toString() || "",
      producao_real: safra.producao_real?.toString() || "",
      status: safra.status || "planejada",
      talhoes: safra.talhoes || []
    });
    setSubmitAttempted(false);
    setTouched({ nome: false, cultura: false, status: false });
    setErrors({});
    setEditingSafra(safra);
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const nextErrors = getErrors(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const data = {
      ...formData,
      area_plantada: formData.area_plantada ? parseFloat(formData.area_plantada) : null,
      producao_esperada: formData.producao_esperada ? parseFloat(formData.producao_esperada) : null,
      producao_real: formData.producao_real ? parseFloat(formData.producao_real) : null
    };

    if (editingSafra) {
      updateMutation.mutate({ id: editingSafra.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const validateNome = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "Nome da safra é obrigatório";
    if (trimmed.length > 255) return "Nome deve ter no máximo 255 caracteres";
    return "";
  };

  const validateCultura = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "Cultura é obrigatória";
    if (trimmed.length > 100) return "Cultura deve ter no máximo 100 caracteres";
    return "";
  };

  const validateStatus = (value) => {
    const trimmed = String(value || "").trim();
    if (trimmed.length > 50) return "Status deve ter no máximo 50 caracteres";
    return "";
  };

  const getErrors = (data) => {
    const next = {};
    const nomeError = validateNome(data.nome);
    const culturaError = validateCultura(data.cultura);
    const statusError = validateStatus(data.status);
    if (nomeError) next.nome = nomeError;
    if (culturaError) next.cultura = culturaError;
    if (statusError) next.status = statusError;
    return next;
  };

  const { data: movInsumos = [] } = useQuery({
    queryKey: ['mov_insumos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.MovimentacaoInsumo.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: movGraos = [] } = useQuery({
    queryKey: ['mov_graos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.MovimentacaoGrao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const getCustoSafra = (safra) => {
    const custoFinanceiro = lancamentos
      .filter(l => String(l.safra_id) === String(safra.id) && l.tipo === 'despesa' && (l.status === 'pago' || !l.status))
      .reduce((acc, l) => acc + (l.valor || 0), 0);

    const custoInsumos = movInsumos
      .filter(m => String(m.safra_id) === String(safra.id))
      .reduce((acc, m) => acc + (Number(m.custo_total) || 0), 0);

    return custoFinanceiro + custoInsumos;
  };

  const getReceitaSafra = (safra) => {
    const receitaFinanceira = lancamentos
      .filter(l => String(l.safra_id) === String(safra.id) && l.tipo === 'receita' && (l.status === 'pago' || !l.status))
      .reduce((acc, l) => acc + (l.valor || 0), 0);

    const receitaGraos = movGraos
      .filter(m => String(m.safra_id) === String(safra.id) && m.tipo === 'saida')
      .reduce((acc, m) => acc + (Number(m.valor_total) || 0), 0);

    return receitaFinanceira + receitaGraos;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const statusConfig = {
    planejada: { label: "Planejada", color: "bg-blue-100 text-blue-700", progress: 10 },
    em_andamento: { label: "Em Andamento", color: "bg-yellow-100 text-yellow-700", progress: 50 },
    finalizada: { label: "Finalizada", color: "bg-green-100 text-green-700", progress: 100 }
  };
  const showNomeError = (touched.nome || submitAttempted) && errors.nome;
  const showCulturaError = (touched.cultura || submitAttempted) && errors.cultura;
  const showStatusError = (touched.status || submitAttempted) && errors.status;

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade para gerenciar as safras</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Wheat className="w-8 h-8 text-[#D4A373]" />
            Safras
          </h1>
          <p className="text-stone-600 mt-1">Acompanhe suas safras e produções</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2"
            >
              <Plus className="w-5 h-5" />
              Nova Safra
            </Button>
          </DialogTrigger>

          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wheat className="w-5 h-5 text-[#D4A373]" />
                {editingSafra ? "Editar Safra" : "Nova Safra"}
              </DialogTitle>
              <DialogDescription>
                Preencha os dados da safra
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Nome da Safra *</Label>
                  <Input
                    placeholder="Ex: Safra 2024/2025"
                    value={formData.nome}
                    onChange={(e) => {
                      const next = { ...formData, nome: e.target.value };
                      setFormData(next);
                      if (submitAttempted || touched.nome) {
                        setErrors((prev) => ({ ...prev, nome: validateNome(next.nome) }));
                      }
                    }}
                    onBlur={() => {
                      setTouched((prev) => ({ ...prev, nome: true }));
                      setErrors((prev) => ({ ...prev, nome: validateNome(formData.nome) }));
                    }}
                    maxLength={255}
                    required
                    aria-invalid={showNomeError ? "true" : "false"}
                    aria-describedby={showNomeError ? "safra-nome-error" : undefined}
                    className={showNomeError ? "border-red-500" : ""}
                  />
                  {showNomeError ? (
                    <span id="safra-nome-error" className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.nome}
                    </span>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Cultura *</Label>
                  <Select
                    value={formData.cultura}
                    onValueChange={(v) => {
                      const next = { ...formData, cultura: v };
                      setFormData(next);
                      setTouched((prev) => ({ ...prev, cultura: true }));
                      if (submitAttempted || touched.cultura) {
                        setErrors((prev) => ({ ...prev, cultura: validateCultura(v) }));
                      }
                    }}
                    required
                  >
                    <SelectTrigger
                      className={showCulturaError ? "border-red-500" : ""}
                      aria-invalid={showCulturaError ? "true" : "false"}
                      aria-describedby={showCulturaError ? "safra-cultura-error" : undefined}
                      onBlur={() => {
                        setTouched((prev) => ({ ...prev, cultura: true }));
                        setErrors((prev) => ({ ...prev, cultura: validateCultura(formData.cultura) }));
                      }}
                    >
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {culturas.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showCulturaError ? (
                    <span id="safra-cultura-error" className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.cultura}
                    </span>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => {
                      const next = { ...formData, status: v };
                      setFormData(next);
                      setTouched((prev) => ({ ...prev, status: true }));
                      if (submitAttempted || touched.status) {
                        setErrors((prev) => ({ ...prev, status: validateStatus(v) }));
                      }
                    }}
                  >
                    <SelectTrigger
                      className={showStatusError ? "border-red-500" : ""}
                      aria-invalid={showStatusError ? "true" : "false"}
                      aria-describedby={showStatusError ? "safra-status-error" : undefined}
                      onBlur={() => {
                        setTouched((prev) => ({ ...prev, status: true }));
                        setErrors((prev) => ({ ...prev, status: validateStatus(formData.status) }));
                      }}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planejada">Planejada</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="finalizada">Finalizada</SelectItem>
                    </SelectContent>
                  </Select>
                  {showStatusError ? (
                    <span id="safra-status-error" className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.status}
                    </span>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Data Início</Label>
                  <Input
                    type="date"
                    value={formData.data_inicio}
                    onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Data Fim</Label>
                  <Input
                    type="date"
                    value={formData.data_fim}
                    onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Área Plantada (ha)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={formData.area_plantada}
                    onChange={(e) => setFormData({ ...formData, area_plantada: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Produção Esperada</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Sacas/Toneladas"
                    value={formData.producao_esperada}
                    onChange={(e) => setFormData({ ...formData, producao_esperada: e.target.value })}
                  />
                </div>

                {editingSafra && (
                  <div className="space-y-2 col-span-2">
                    <Label>Produção Real</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Produção colhida"
                      value={formData.producao_real}
                      onChange={(e) => setFormData({ ...formData, producao_real: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingSafra ? "Atualizar" : "Criar Safra"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Empty State */}
      {safras.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <div className="w-20 h-20 bg-[#D4A373]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Wheat className="w-10 h-10 text-[#D4A373]" />
          </div>
          <h3 className="text-xl font-bold text-stone-800 mb-2">Nenhuma safra cadastrada</h3>
          <p className="text-stone-600 mb-6">Cadastre sua primeira safra para começar</p>
        </div>
      )}

      {/* Safras Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnimatePresence>
          {safras.map((safra, index) => {
            const custo = getCustoSafra(safra);
            const receita = getReceitaSafra(safra);
            const status = statusConfig[safra.status];
            const custoHectare = safra.area_plantada ? custo / safra.area_plantada : 0;

            return (
              <motion.div
                key={safra.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  className="bg-white border-stone-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => navigate(`/Safras/${safra.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-stone-800">{safra.nome}</h3>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-[#D4A373]/20 text-[#BC8A5F] border-[#D4A373]/30">
                            {safra.cultura}
                          </Badge>
                          <Badge className={status.color}>{status.label}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(safra)}
                          className="text-stone-400 hover:text-stone-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMutation.mutate(safra.id)}
                          className="text-stone-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Progresso</span>
                        <span className="font-medium">{status.progress}%</span>
                      </div>
                      <Progress value={status.progress} className="h-2" />
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-stone-400" />
                        <div>
                          <p className="text-xs text-stone-500">Área</p>
                          <p className="font-semibold text-stone-800">
                            {safra.area_plantada ? `${safra.area_plantada} ha` : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-stone-400" />
                        <div>
                          <p className="text-xs text-stone-500">Produção Esperada</p>
                          <p className="font-semibold text-stone-800">
                            {safra.producao_esperada || '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <div>
                          <p className="text-xs text-stone-500">Período</p>
                          <p className="font-semibold text-stone-800 text-sm">
                            {safra.data_inicio ? format(new Date(safra.data_inicio), "dd/MM/yy") : '-'}
                            {safra.data_fim && ` a ${format(new Date(safra.data_fim), "dd/MM/yy")}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-stone-400" />
                        <div>
                          <p className="text-xs text-stone-500">Custo/ha</p>
                          <p className="font-semibold text-stone-800">
                            {formatCurrency(custoHectare)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="pt-4 border-t border-stone-100 grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-red-50 rounded-xl">
                        <p className="text-xs text-red-600 mb-1">Custos</p>
                        <p className="font-bold text-red-700">{formatCurrency(custo)}</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-xl">
                        <p className="text-xs text-green-600 mb-1">Receitas</p>
                        <p className="font-bold text-green-700">{formatCurrency(receita)}</p>
                      </div>
                      <div className={`text-center p-3 rounded-xl ${receita - custo >= 0 ? 'bg-[#40916C]/10' : 'bg-stone-100'}`}>
                        <p className="text-xs text-stone-600 mb-1">Resultado</p>
                        <p className={`font-bold ${receita - custo >= 0 ? 'text-[#40916C]' : 'text-red-600'}`}>
                          {formatCurrency(receita - custo)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
